/**
 * Every racer of the race as the views and the HUD see them in one frame: the local run
 * (interpolated) and the others (ghost samples, or standing at the start of their level until
 * something is heard from them), their looks, and how far along the race they are.
 *
 * "How far along" is measured against the race's levels; against the clock, where nobody gets
 * to the last level, against a track as long as the race so far (setSpan).
 */
import type { PlayerId, PlayerProfile, RaceProgressEntry, RoomMember } from '@cube-rush/shared';
import { RacerArt } from '../render/racer';
import { createGhostPose, type GhostStore, STALE_AFTER_MS } from './ghosts';
import { MIN_STRIP_SPAN, type StripRacer } from './hud/hudModel';
import type { ViewCandidate } from './layout';
import type { LevelResources } from './levelResources';
import type { LocalRun, RunPose } from './localRun';
import { completedLevels, levelFraction, overallProgress } from './standings';
import type { RacerSnapshot } from './views';

/** The look of somebody the room does not know (left already, or a newer server). */
const FALLBACK_PROFILE: PlayerProfile = {
  name: '?',
  avatar: 'robot',
  skin: { icon: 'classic', primary: 'gray', secondary: 'white', trail: 'none' },
};

export function findEntry(
  progress: readonly RaceProgressEntry[],
  id: PlayerId,
): RaceProgressEntry | undefined {
  for (const entry of progress) if (entry.id === id) return entry;
  return undefined;
}

export class Racers {
  /** Every racer this frame, the local player first (the objects are reused). */
  readonly list: RacerSnapshot[] = [];
  private readonly arts = new Map<PlayerId, RacerArt>();
  private readonly snapshots = new Map<PlayerId, RacerSnapshot>();
  /** Others whose position came from their own reports (not just the level start). */
  private readonly heard = new Set<PlayerId>();
  private readonly ghostPose = createGhostPose();
  private readonly runPose: RunPose = { x: 0, y: 0, rot: 0 };
  /** Levels overall progress is measured against (see setSpan). */
  private span: number;

  constructor(
    private readonly me: PlayerId,
    private readonly myProfile: () => PlayerProfile,
    private readonly members: () => readonly RoomMember[],
    /** Participants other than the local player. */
    readonly others: readonly PlayerId[],
    private readonly levels: LevelResources | null,
    private readonly levelCount: number,
    /** A race against the clock: the strip shows everybody's levels done. */
    private readonly timed = false,
  ) {
    this.span = timed ? MIN_STRIP_SPAN : levelCount;
  }

  /**
   * Against the clock: how many levels overall progress spans (the track of the race strip,
   * the view picker), the same for everybody in a frame.
   */
  setSpan(span: number): void {
    this.span = Math.max(1, span);
  }

  /** The furthest level (0-based) anybody is on: the local run, a ghost, the server's word. */
  highestLevel(progress: readonly RaceProgressEntry[], run: LocalRun | null): number {
    let highest = run ? run.sim.levelIndex : 0;
    for (const entry of progress) highest = Math.max(highest, entry.lvl);
    for (const id of this.others) {
      const snap = this.snapshots.get(id);
      if (snap && this.heard.has(id)) highest = Math.max(highest, snap.level);
    }
    return Math.min(highest, Math.max(0, this.levelCount - 1));
  }

  get(id: PlayerId): RacerSnapshot | undefined {
    return this.snapshots.get(id);
  }

  art(id: PlayerId): RacerArt {
    let art = this.arts.get(id);
    if (!art) {
      art = new RacerArt(this.profileOf(id));
      this.arts.set(id, art);
    }
    return art;
  }

  /** New looks for racers who changed their profile (`members`: the room right now). */
  refreshArts(members: readonly RoomMember[]): void {
    for (const [id, art] of this.arts) {
      const profile =
        id === this.me
          ? this.myProfile()
          : (members.find((m) => m.id === id)?.profile ?? FALLBACK_PROFILE);
      if (art.matches(profile)) continue;
      const fresh = new RacerArt(profile);
      this.arts.set(id, fresh);
      const snap = this.snapshots.get(id);
      if (snap) snap.art = fresh;
    }
  }

  /**
   * Samples everybody for a frame: the local run at engine-clock `now`, the others at race
   * time `ghostTime` (GhostStore.delayMs in the past; their staleness too).
   */
  sample(
    run: LocalRun | null,
    now: number,
    ghosts: GhostStore,
    ghostTime: number,
    progress: readonly RaceProgressEntry[],
  ): void {
    const list = this.list;
    list.length = 0;
    if (run) list.push(this.sampleLocal(run, now));
    const pose = this.ghostPose;
    for (const id of this.others) {
      const snap = this.snapshot(id);
      const entry = findEntry(progress, id);
      if (ghosts.sample(id, ghostTime, pose)) {
        this.heard.add(id);
        snap.x = pose.x;
        snap.y = pose.y;
        snap.rot = pose.rot;
        snap.mode = pose.mode;
        snap.gravity = pose.grav;
        snap.alive = pose.alive;
        snap.fx = pose.fx;
        snap.level = pose.lvl;
        snap.attempt = pose.att;
        snap.stale = ghosts.isStale(id, ghostTime) || entry?.quit === true;
      } else {
        // Nothing heard yet: standing at the start of the level the server knows about.
        const level = this.levels?.get(entry?.lvl ?? 0).level;
        snap.level = Math.min(entry?.lvl ?? 0, Math.max(0, this.levelCount - 1));
        snap.x = level?.start.x ?? 0;
        snap.y = level?.start.y ?? 0.5;
        snap.rot = 0;
        snap.mode = level?.start.mode ?? 'cube';
        snap.gravity = level?.start.gravity ?? 1;
        snap.alive = true;
        snap.fx = 0;
        snap.attempt = 1;
        snap.stale =
          ghostTime > STALE_AFTER_MS || entry?.quit === true || (entry?.finishTm ?? null) !== null;
      }
      list.push(snap);
    }
  }

  /** Overall progress 0..1 (the live position, else the level the server knows about). */
  overall(id: PlayerId, progress: readonly RaceProgressEntry[], run: LocalRun | null): number {
    const span = this.span;
    const entry = findEntry(progress, id);
    if (entry && (entry.finishTm !== null || entry.lvl >= this.levelCount)) return 1;
    if (id === this.me && run) {
      const sim = run.sim;
      if (sim.finished) return 1;
      return overallProgress(sim.levelIndex, sim.progress, span);
    }
    const snap = this.snapshots.get(id);
    const levels = this.levels;
    if (snap && levels && !snap.stale && this.heard.has(id)) {
      return overallProgress(snap.level, levelFraction(levels.get(snap.level).level, snap.x), span);
    }
    return overallProgress(entry?.lvl ?? 0, 0, span);
  }

  /** The others as candidates for a view: still racing and sending positions? */
  candidates(progress: readonly RaceProgressEntry[], run: LocalRun | null): ViewCandidate[] {
    return this.others.map((id) => {
      const entry = findEntry(progress, id);
      const snap = this.snapshots.get(id);
      return {
        id,
        progress: this.overall(id, progress, run),
        active:
          snap !== undefined &&
          !snap.stale &&
          entry?.quit !== true &&
          (entry?.finishTm ?? null) === null,
      };
    });
  }

  /**
   * Everybody on the race progress strip. Against the clock with their levels done: the local
   * run's own count (at once), the server's for the others.
   */
  strip(
    participants: readonly PlayerId[],
    progress: readonly RaceProgressEntry[],
    run: LocalRun | null,
  ): StripRacer[] {
    const members = this.members();
    const ids = [...participants];
    if (run && !ids.includes(this.me)) ids.push(this.me);
    return ids.map((id) => {
      const entry = findEntry(progress, id);
      const member = members.find((m) => m.id === id);
      const art = this.art(id);
      const local = id === this.me && run !== null;
      return {
        id,
        avatar: art.profile.avatar,
        name: art.profile.name,
        color: art.accent,
        me: id === this.me,
        finished: entry ? entry.finishTm !== null || entry.lvl >= this.levelCount : false,
        quit: entry?.quit ?? false,
        offline: id !== this.me && member !== undefined && !member.connected,
        levels: !this.timed ? null : local ? completedLevels(run.sim) : (entry?.lvl ?? 0),
      };
    });
  }

  private sampleLocal(run: LocalRun, now: number): RacerSnapshot {
    const snap = this.snapshot(this.me);
    const pose = run.renderPose(now, this.runPose);
    const sim = run.sim;
    const s = sim.state;
    snap.x = pose.x;
    snap.y = pose.y;
    snap.rot = pose.rot;
    snap.mode = s.mode;
    snap.gravity = s.gravity;
    snap.alive = s.alive;
    snap.fx = sim.activeBoostBits();
    snap.level = sim.levelIndex;
    snap.attempt = sim.attempt;
    snap.stale = false;
    return snap;
  }

  private snapshot(id: PlayerId): RacerSnapshot {
    let snap = this.snapshots.get(id);
    if (!snap) {
      snap = {
        id,
        x: 0,
        y: 0,
        rot: 0,
        mode: 'cube',
        gravity: 1,
        alive: true,
        fx: 0,
        level: 0,
        attempt: 1,
        stale: false,
        art: this.art(id),
      };
      this.snapshots.set(id, snap);
    }
    return snap;
  }

  private profileOf(id: PlayerId): PlayerProfile {
    if (id === this.me) return this.myProfile();
    return this.members().find((m) => m.id === id)?.profile ?? FALLBACK_PROFILE;
  }
}
